import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab
} from '@mui/material';
import {
    Cancel as CancelIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Restore as RestoreIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axiosInstance from '../utils/axios';

const CourierPenalties = ({ courierId, courierName, open, onClose }) => {
    const [penalties, setPenalties] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cancelDialog, setCancelDialog] = useState({ open: false, penalty: null, type: null });
    const [cancellationReason, setCancellationReason] = useState('');
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        if (courierId) {
            fetchPenalties();
        }
    }, [courierId]);

    const fetchPenalties = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/courier-stats/${courierId}/penalties`);
            setPenalties(response.data.data.penalties);
            setSummary(response.data.data.summary);
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при загрузке штрафов');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelPenalty = async () => {
        try {
            const { penalty, type } = cancelDialog;
            
            if (type === 'system') {
                await axiosInstance.put(`/courier-stats/system-penalty/${penalty.id}/cancel`, {
                    cancellationReason
                });
            } else {
                await axiosInstance.put(`/courier-stats/penalty/${penalty.id}/cancel`, {
                    cancellationReason
                });
            }

            setCancelDialog({ open: false, penalty: null, type: null });
            setCancellationReason('');
            fetchPenalties();
            
            // Уведомляем родительский компонент о необходимости обновления данных
            if (onClose && typeof onClose === 'function') {
                onClose(true); // true означает что нужно обновить данные
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при аннулировании штрафа');
        }
    };

    const handleRestorePenalty = async (penalty) => {
        try {
            const restoreReason = prompt('Укажите причину восстановления штрафа:');
            if (!restoreReason) return;

            await axiosInstance.put(`/courier-stats/system-penalty/${penalty.id}/restore`, {
                restoreReason
            });

            fetchPenalties();
            
            // Уведомляем родительский компонент о необходимости обновления данных
            if (onClose && typeof onClose === 'function') {
                onClose(true); // true означает что нужно обновить данные
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при восстановлении штрафа');
        }
    };

    const openCancelDialog = (penalty) => {
        setCancelDialog({ 
            open: true, 
            penalty, 
            type: penalty.type 
        });
    };

    const formatDate = (date) => {
        return format(new Date(date), 'dd.MM.yyyy HH:mm', { locale: ru });
    };

    const getPenaltyTypeChip = (type) => {
        if (type === 'system') {
            return (
                <Chip 
                    label="Системный" 
                    color="warning" 
                    size="small"
                    icon={<WarningIcon />}
                />
            );
        }
        return (
            <Chip 
                label="Дополнительный" 
                color="info" 
                size="small"
                icon={<InfoIcon />}
            />
        );
    };

    const filteredPenalties = penalties.filter(penalty => {
        if (tabValue === 0) return true; // Все
        if (tabValue === 1) return penalty.type === 'system'; // Системные
        if (tabValue === 2) return penalty.type === 'additional'; // Дополнительные
        return true;
    });

    if (loading) return <Typography>Загрузка...</Typography>;

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        Штрафы курьера: {courierName}
                    </Typography>
                    <Button variant="outlined" onClick={() => onClose(false)}>
                        Закрыть
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ minHeight: '500px' }}>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Сводка */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="warning.main">
                                    {summary.totalSystemPenalties || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Системных штрафов
                                </Typography>
                                <Typography variant="body2" color="warning.main">
                                    {summary.totalSystemAmount || 0} ₽
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="info.main">
                                    {summary.totalAdditionalPenalties || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Дополнительных штрафов
                                </Typography>
                                <Typography variant="body2" color="info.main">
                                    {summary.totalAdditionalAmount || 0} ₽
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="error.main">
                                    {(summary.totalSystemPenalties || 0) + (summary.totalAdditionalPenalties || 0)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Всего штрафов
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                    {summary.totalAmount || 0} ₽
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Фильтры */}
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                    <Tab label={`Все (${penalties.length})`} />
                    <Tab label={`Системные (${penalties.filter(p => p.type === 'system').length})`} />
                    <Tab label={`Дополнительные (${penalties.filter(p => p.type === 'additional').length})`} />
                </Tabs>

                {/* Таблица штрафов */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Тип</TableCell>
                                <TableCell>Дата</TableCell>
                                <TableCell>Сумма</TableCell>
                                <TableCell>Причина</TableCell>
                                <TableCell>Детали</TableCell>
                                <TableCell>Создал</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPenalties.map((penalty) => (
                                <TableRow key={`${penalty.type}-${penalty.id}`}>
                                    <TableCell>
                                        {getPenaltyTypeChip(penalty.type)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(penalty.penaltyDate)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            color="error.main"
                                            fontWeight="bold"
                                        >
                                            -{penalty.amount} ₽
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {penalty.reason}
                                    </TableCell>
                                    <TableCell>
                                        {penalty.type === 'system' ? (
                                            <Box>
                                                <Typography variant="body2">
                                                    Заказ: {penalty.orderId}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Фасовка: {penalty.packagingName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Адрес: {penalty.addressName}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Создан: {formatDate(penalty.createdAt)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {penalty.createdBy || 'Система'}
                                    </TableCell>
                                    <TableCell>
                                        {penalty.type === 'system' ? (
                                            penalty.isActive ? (
                                                <Tooltip title="Аннулировать штраф">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => openCancelDialog(penalty)}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Восстановить штраф">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleRestorePenalty(penalty)}
                                                    >
                                                        <RestoreIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        ) : (
                                            penalty.canCancel && penalty.isActive ? (
                                                <Tooltip title="Аннулировать штраф">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => openCancelDialog(penalty)}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Штраф уже аннулирован">
                                                    <IconButton disabled>
                                                        <CheckCircleIcon color="success" />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredPenalties.length === 0 && (
                    <Box textAlign="center" py={4}>
                        <Typography variant="body1" color="text.secondary">
                            Штрафы не найдены
                        </Typography>
                    </Box>
                )}
                </Box>
            </DialogContent>

            {/* Диалог аннулирования штрафа */}
            <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, penalty: null, type: null })}>
                <DialogTitle>
                    Аннулировать {cancelDialog.type === 'system' ? 'системный' : 'дополнительный'} штраф
                </DialogTitle>
                <DialogContent>
                    {cancelDialog.penalty && (
                        <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                                Сумма штрафа: <strong>{cancelDialog.penalty.amount} ₽</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Причина: {cancelDialog.penalty.reason}
                            </Typography>
                            {cancelDialog.type === 'system' && (
                                <Typography variant="body2" color="text.secondary">
                                    Заказ: {cancelDialog.penalty.orderId}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        label="Причина аннулирования"
                        multiline
                        rows={3}
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialog({ open: false, penalty: null, type: null })}>
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleCancelPenalty}
                        color="error"
                        disabled={!cancellationReason.trim()}
                    >
                        Аннулировать
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default CourierPenalties;
